import { useEffect, useState, useCallback } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import apiClient from '../../lib/api';

interface OverrideAuditRow {
  id: string;
  title: string | null;
  start_time: string;
  end_time: string;
  created_at: string;
  assigned_to_id: string | null;
  created_by: string | null;
  overrides: Record<string, unknown> | null;
  force_outside_shift: boolean | null;
  force_without_clockin: boolean | null;
  created_by_name: string | null;
  created_by_email: string | null;
  assigned_to_name: string | null;
  assigned_to_email: string | null;
  type_name: string | null;
  audit_user_id: string | null;
  api_key_id: string | null;
  reason: string | null;
  audit_at: string | null;
  audit_ip: string | null;
  audit_user_name: string | null;
  audit_user_email: string | null;
  api_key_name: string | null;
  api_key_prefix: string | null;
}

type FlagFilter = 'all' | 'outside_shift' | 'without_clockin';

const DAY_PRESETS: Array<{ value: number; label: string }> = [
  { value: 7, label: '7 giorni' },
  { value: 14, label: '14 giorni' },
  { value: 30, label: '30 giorni' },
  { value: 60, label: '60 giorni' },
  { value: 90, label: '90 giorni' },
];

export default function OverrideAuditPanel() {
  const [rows, setRows] = useState<OverrideAuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState<number>(30);
  const [flag, setFlag] = useState<FlagFilter>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('days', String(days));
      if (flag !== 'all') params.set('flag', flag);
      const r = await apiClient.get<OverrideAuditRow[]>(
        `/calendar/appointments/override-audit?${params.toString()}`
      );
      if (r.success && r.data) setRows(r.data);
      else setError(r.error || 'Errore nel caricamento');
    } catch (e) {
      setError((e as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [days, flag]);

  useEffect(() => {
    load();
  }, [days, flag, load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Audit appuntamenti forzati
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Prenotazioni create fuori turno o senza timbratura, con motivazione e operatore.
          </p>
        </div>
        <div className="ml-auto flex items-end gap-2">
          <div>
            <label htmlFor="override-audit-days" className="block text-xs text-gray-500 dark:text-gray-400">Periodo</label>
            <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
              <SelectTrigger id="override-audit-days" className="h-8 w-[160px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAY_PRESETS.map((p) => (
                  <SelectItem key={p.value} value={String(p.value)}>
                    Ultimi {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label htmlFor="override-audit-flag" className="block text-xs text-gray-500 dark:text-gray-400">Tipo override</label>
            <Select value={flag} onValueChange={(v) => setFlag(v as FlagFilter)}>
              <SelectTrigger id="override-audit-flag" className="h-8 w-[160px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="outside_shift">Fuori turno</SelectItem>
                <SelectItem value="without_clockin">Senza timbratura</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button
            onClick={load}
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
          >
            Aggiorna
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500">Caricamento…</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="p-2 text-left">Data / Ora</th>
              <th className="p-2 text-left">Appuntamento</th>
              <th className="p-2 text-left">Consulente</th>
              <th className="p-2 text-left">Override</th>
              <th className="p-2 text-left">Motivazione</th>
              <th className="p-2 text-left">Forzato da</th>
              <th className="p-2 text-left">Quando</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {rows.map((r) => {
              const flags = [
                r.force_outside_shift ? 'Fuori turno' : null,
                r.force_without_clockin ? 'Senza timbratura' : null,
              ].filter(Boolean);

              const actorName = r.audit_user_name?.trim() || r.created_by_name?.trim() || '';
              const actorEmail = r.audit_user_email || r.created_by_email || '';
              const apiKeyLabel = r.api_key_name
                ? `${r.api_key_name}${r.api_key_prefix ? ` (${r.api_key_prefix})` : ''}`
                : r.api_key_id
                ? `API key ${r.api_key_id.slice(0, 8)}…`
                : null;

              return (
                <tr key={r.id}>
                  <td className="p-2 align-top whitespace-nowrap">
                    <div className="font-medium">
                      {new Date(r.start_time).toLocaleString('it-IT')}
                    </div>
                    <div className="text-xs text-gray-500">
                      →{' '}
                      {new Date(r.end_time).toLocaleTimeString('it-IT', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </td>
                  <td className="p-2 align-top">
                    <div className="font-medium">{r.title || '—'}</div>
                    <div className="text-xs text-gray-500">{r.type_name || ''}</div>
                  </td>
                  <td className="p-2 align-top">
                    <div>{r.assigned_to_name?.trim() || '—'}</div>
                    <div className="text-xs text-gray-500">{r.assigned_to_email || ''}</div>
                  </td>
                  <td className="p-2 align-top">
                    {flags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {flags.map((f) => (
                          <span
                            key={f}
                            className="inline-block rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="p-2 align-top max-w-md">
                    <div className="whitespace-pre-wrap break-words">
                      {r.reason || (
                        <span className="text-gray-400">— nessuna motivazione —</span>
                      )}
                    </div>
                  </td>
                  <td className="p-2 align-top">
                    {apiKeyLabel ? (
                      <>
                        <div className="font-medium">{apiKeyLabel}</div>
                        <div className="text-xs text-gray-500">API key</div>
                      </>
                    ) : actorName || actorEmail ? (
                      <>
                        <div>{actorName || '—'}</div>
                        <div className="text-xs text-gray-500">{actorEmail}</div>
                      </>
                    ) : r.audit_user_id ? (
                      <div className="text-xs text-gray-500">
                        Utente {r.audit_user_id.slice(0, 8)}…
                      </div>
                    ) : (
                      '—'
                    )}
                    {r.audit_ip && (
                      <div className="text-xs text-gray-400">IP {r.audit_ip}</div>
                    )}
                  </td>
                  <td className="p-2 align-top whitespace-nowrap text-xs text-gray-500">
                    {r.audit_at
                      ? new Date(r.audit_at).toLocaleString('it-IT')
                      : new Date(r.created_at).toLocaleString('it-IT')}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="p-4 text-center text-gray-500">
                  Nessun appuntamento forzato nel periodo selezionato
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
